import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Search,
  Plus,
  Minus,
  Trash2,
  CreditCard,
  DollarSign,
  Receipt,
  Users,
  Clock,
  ChefHat,
  Grid3X3,
  List,
  Loader2,
  AlertTriangle,
  Tag,
  PauseCircle,
  PlayCircle,
  Settings2,
  WifiOff,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { getAssetPath } from '@/lib/subdomain';
import { useProducts } from '@/hooks/useProducts';
import { useLocationStore } from '@/store/location';
import { useCurrency } from '@/hooks/useCurrency';
import { useOrders } from '@/hooks/useOrders';
import { usePaymentTypes } from '@/hooks/usePaymentTypes';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useRegisterSession } from '@/hooks/useRegisterSession';
import { useVirtualSession } from '@/hooks/useVirtualSession';
import { supabase } from '@/lib/supabase';
import { AddCustomerModal } from '@/components/AddCustomerModal';
import { useNavigate } from 'react-router-dom';
import { useUpsellSuggestions, UpsellSuggestion } from '@/hooks/useUpsellSuggestions';
import { UpsellPopover } from '@/components/pos/UpsellPopover';
import { ModifierSelectionModal } from '@/components/pos/ModifierSelectionModal';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useReceiptPrint } from '@/components/orders/ReceiptPrintView';
import { SelectedModifier } from '@/hooks/useModifiers';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { LocationSelector } from '@/components/location/LocationSelector';
import { SplitPaymentDialog } from '@/components/checkout/SplitPaymentDialog';
import { useInventory } from '@/hooks/useInventory';
import { useLocationSettings } from '@/hooks/useLocationSettings';
import { useActivePromotions, calculatePromotionDiscounts } from '@/hooks/usePromotionEngine';
import type { CartItem as PromotionCartItem, PromotionResult } from '@/hooks/usePromotionEngine';
import { ManagerPinDialog } from '@/components/ManagerPinDialog';
import { useRemoteSessionHost } from '@/hooks/useRemoteSession';
import { 
  RemoteSessionHost, 
  IncomingSessionRequest, 
  RemoteSessionBanner,
  RequestSupportCompact 
} from '@/components/remote';
import { useTrainingModeStore } from '@/store/trainingMode';
import { TrainingModeBanner } from '@/components/pos/TrainingModeBanner';
import { TrainingModeSwitch } from '@/components/pos/TrainingModeSwitch';
import { TrainingModeTutorial } from '@/components/pos/TrainingModeTutorial';

type PaymentMethod = 'cash' | 'card' | 'gift_card' | 'loyalty' | 'store_credit' | 'other';
type CheckoutPaymentMethod = Exclude<PaymentMethod, 'loyalty'>;

const ORDER_DRAFT_KEY = 'blazepos-pos-draft';
const PARKED_SALES_KEY = 'blazepos-parked-sales';
const PAYMENT_AMOUNT_TOLERANCE = 0.005;

const roundCurrency = (amount: number) => Math.round((amount + Number.EPSILON) * 100) / 100;

type ParkedSale = {
  id: string;
  name: string;
  items: OrderItem[];
  orderType: 'dine-in' | 'takeout' | 'delivery';
  tableNumber: string;
  customer: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
  total: number;
  createdAt: string;
};

type CheckoutPayment = {
  id: string;
  paymentTypeId: string;
  paymentTypeName: string;
  paymentMethod: CheckoutPaymentMethod;
  amount: number;
  cashTendered?: number;
};

interface OrderItem {
  id: string;
  product_id: string;
  name: string;
  price: number;  // Original price (including modifiers)
  base_price: number;  // Base product price without modifiers
  effective_price: number;  // Price after capping (for subsidies)
  quantity: number;
  notes?: string;
  is_subsidy: boolean;
  subsidy_capped: boolean;
  original_subsidy_amount?: number;  // For tracking
  applied_subsidy_amount?: number;   // For tracking
  modifiers?: SelectedModifier[];  // Selected modifiers for this item
}

// Helper to check if product is a subsidy (negative price)
const isSubsidy = (price: number): boolean => price < 0;

export default function POS() {
  const navigate = useNavigate();
  const { currentLocation } = useLocationStore();
  const { products, categories, isLoading } = useProducts();
  const { inventoryItems, getFOHStock } = useInventory();
  const { fohStockWarningBehavior } = useLocationSettings();
  const { formatCurrency, currency } = useCurrency();
  const { createOrderOffline, addPayment, completeOrder, isOnline } = useOrders(currentLocation?.id || null);
  const { locationPaymentTypes, hideDefaultPaymentTypes } = usePaymentTypes();
  const { user } = useAuth();
  const { effectiveRole } = usePermissions();
  const { isRegisterOpen, isLoading: registerLoading } = useRegisterSession();
  const { flags } = useFeatureFlags();
  
  // Virtual session for admins/managers without a physical register
  const { 
    activeSession, 
    isVirtualSession, 
    canUseVirtualSession, 
    createVirtualSession,
    isLoading: virtualSessionLoading 
  } = useVirtualSession();

  // Promotions engine - fetch active promotions for this location
  const { data: activePromotions = [] } = useActivePromotions(currentLocation?.id || null);

  // Remote session support (for receiving remote control from admins)
  const {
    connectionState: remoteConnectionState,
    localStream: remoteLocalStream,
    currentSession: remoteSession,
    pendingRequest: remotePendingRequest,
    error: remoteError,
    myPendingRequest,
    hasPendingRequest,
    acceptSession,
    rejectSession,
    createSupportRequest,
    cancelSupportRequest,
    endSession: endRemoteSession,
    isScreenShareSupported,
  } = useRemoteSessionHost();

  // Training Mode
  const { isTrainingMode, getNextOrderNumber, addTrainingOrder } = useTrainingModeStore();

  // Check if user is a cashier/employee (needs open register)
  const isCashier = effectiveRole === 'cashier' || effectiveRole === 'employee';

  // Check if user can manually send to kitchen (admins, managers, owners only)
  // Cashiers/employees should not see this - orders auto-send to kitchen after payment
  const canSendToKitchen = ['superadmin', 'primary_admin', 'owner', 'admin', 'manager'].includes(effectiveRole || '');
  
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Measure the POS area so we can collapse to a single-column, search-driven
  // layout (Lightspeed-style) when there isn't room for the product grid and
  // the order panel side by side, instead of squishing the product cards.
  const posAreaRef = useRef<HTMLDivElement>(null);
  const [posAreaWidth, setPosAreaWidth] = useState(0);
  useEffect(() => {
    const el = posAreaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setPosAreaWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const compactPOS = posAreaWidth > 0 && posAreaWidth < 640;
  const [orderType, setOrderType] = useState<'dine-in' | 'takeout' | 'delivery'>('dine-in');
  const [tableNumber, setTableNumber] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [subsidyAuditPending, setSubsidyAuditPending] = useState<{
    productId: string;
    productName: string;
    originalAmount: number;
    appliedAmount: number;
    unusedAmount: number;
  }[]>([]);
  
  // Customer selection state
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<{
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
  } | null>(null);

  // Upsell suggestions state
  const [upsellSuggestions, setUpsellSuggestions] = useState<UpsellSuggestion[]>([]);
  const [lastAddedProduct, setLastAddedProduct] = useState<{ id: string; name: string } | null>(null);
  const { getSuggestions, trackShown, trackAccepted } = useUpsellSuggestions();
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isSplitPaymentOpen, setIsSplitPaymentOpen] = useState(false);
  const [cashTendered, setCashTendered] = useState('');
  const [checkoutPayments, setCheckoutPayments] = useState<CheckoutPayment[]>([]);
  
    const [editingQuantityId, setEditingQuantityId] = useState<string | null>(null);
  const [editingQuantityValue, setEditingQuantityValue] = useState('');
  const [parkedSales, setParkedSales] = useState<ParkedSale[]>([]);
  const { printReceipt } = useReceiptPrint();
  const [shouldAutoPrint] = useState(true);
  const [parkPopoverOpen, setParkPopoverOpen] = useState(false);

  // Modifier selection state
  const [modifierModalOpen, setModifierModalOpen] = useState(false);
  const [selectedProductForModifiers, setSelectedProductForModifiers] = useState<typeof products[0] | null>(null);
  const [productModifiersCache, setProductModifiersCache] = useState<Record<string, boolean>>({});

  // Stock warning override state (for require_pin behavior)
  const [stockOverrideDialogOpen, setStockOverrideDialogOpen] = useState(false);
  const [pendingStockOverride, setPendingStockOverride] = useState<{
    product: typeof products[0];
    modifiers: SelectedModifier[];
    quantity: number;
    fohStock: number;
    cartQuantity: number;
  } | null>(null);

  // Fetch product modifiers cache on mount (to know which products have modifiers)
  useEffect(() => {
    const fetchProductModifiers = async () => {
      if (!currentLocation?.id) return;

      const { data, error } = await supabase
        .from('product_modifier_groups')
        .select('product_id')
        .in('product_id', (products || []).map(p => p.id));

      if (!error && data) {
        const cache: Record<string, boolean> = {};
        data.forEach(item => {
          cache[item.product_id] = true;
        });
        setProductModifiersCache(cache);
      }
    };

    if (products && products.length > 0) {
      fetchProductModifiers();
    }
  }, [products, currentLocation?.id]);

  const filteredProducts = useMemo(() => {
    let filtered = products || [];
    
    if (selectedCategory) {
      filtered = filtered.filter(p => p.category_id === selectedCategory);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [products, selectedCategory, searchQuery]);
  const cashPaymentTypes = useMemo(
    () => locationPaymentTypes.filter((pt) => pt.is_cash_equivalent),
    [locationPaymentTypes]
  );
  const nonCashPaymentTypes = useMemo(
    () => locationPaymentTypes.filter((pt) => !pt.is_cash_equivalent),
    [locationPaymentTypes]
  );
  const loadParkedSales = useCallback(() => {
    try {
      const stored = localStorage.getItem(PARKED_SALES_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as ParkedSale[];
        setParkedSales(parsed);
      }
    } catch (error) {
      console.error('Failed to load parked sales', error);
    }
  }, []);

  const formatCurrencyCents = useCallback(
    (amount: number) =>
      new Intl.NumberFormat('en-JM', {
        style: 'currency',
        currency: currency || 'JMD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount),
    [currency]
  );

  // Load parked sales on mount
  useEffect(() => {
    loadParkedSales();
  }, [loadParkedSales]);

  // Calculate subtotal of non-subsidy items only (for capping calculation)
  const calculateNonSubsidySubtotal = (items: OrderItem[]): number => {
    return items
      .filter(item => !item.is_subsidy)
      .reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  // Recalculate subsidies when order changes to apply capping
  const recalculateSubsidies = (items: OrderItem[]): OrderItem[] => {
    const nonSubsidyTotal = calculateNonSubsidySubtotal(items);
    let remainingBudget = nonSubsidyTotal;
    const newAuditPending: typeof subsidyAuditPending = [];
    
    // Process subsidies in order they were added
    const updatedItems = items.map(item => {
      if (!item.is_subsidy) return item;
      
      const originalSubsidyValue = Math.abs(item.price) * item.quantity;
      
      if (remainingBudget <= 0) {
        // No budget left, cap subsidy to 0
        if (originalSubsidyValue > 0) {
          newAuditPending.push({
            productId: item.product_id,
            productName: item.name,
            originalAmount: item.price * item.quantity,
            appliedAmount: 0,
            unusedAmount: originalSubsidyValue,
          });
        }
        return {
          ...item,
          effective_price: 0,
          subsidy_capped: true,
          original_subsidy_amount: item.price,
          applied_subsidy_amount: 0,
        };
      }
      
      if (originalSubsidyValue > remainingBudget) {
        // Partial cap - subsidy exceeds remaining budget
        const appliedAmount = -remainingBudget / item.quantity;
        const unusedAmount = originalSubsidyValue - remainingBudget;
        
        newAuditPending.push({
          productId: item.product_id,
          productName: item.name,
          originalAmount: item.price * item.quantity,
          appliedAmount: appliedAmount * item.quantity,
          unusedAmount: unusedAmount,
        });
        
        remainingBudget = 0;
        return {
          ...item,
          effective_price: appliedAmount,
          subsidy_capped: true,
          original_subsidy_amount: item.price,
          applied_subsidy_amount: appliedAmount,
        };
      }
      
      // Full subsidy can be applied
      remainingBudget -= originalSubsidyValue;
      return {
        ...item,
        effective_price: item.price,
        subsidy_capped: false,
        original_subsidy_amount: item.price,
        applied_subsidy_amount: item.price,
      };
    });
    
    // Update audit pending state if there are changes
    if (newAuditPending.length > 0) {
      setSubsidyAuditPending(newAuditPending);
    } else {
      setSubsidyAuditPending([]);
    }
    
    return updatedItems;
  };

  const persistDraft = useCallback(
    (draft: {
      items: OrderItem[];
      orderType: typeof orderType;
      tableNumber: string;
      customer: typeof selectedCustomer;
    } | null) => {
      if (flags.autoClearPosOrderOnLeave) {
        localStorage.removeItem(ORDER_DRAFT_KEY);
        return;
      }
      if (!draft || draft.items.length === 0) {
        localStorage.removeItem(ORDER_DRAFT_KEY);
        return;
      }
      try {
        localStorage.setItem(ORDER_DRAFT_KEY, JSON.stringify(draft));
      } catch (error) {
        console.error('Failed to save draft order', error);
      }
    },
    [flags.autoClearPosOrderOnLeave]
  );

  // Load draft order on mount unless auto-clear is enabled
  useEffect(() => {
    if (flags.autoClearPosOrderOnLeave) {
      localStorage.removeItem(ORDER_DRAFT_KEY);
      return;
    }
    try {
      const stored = localStorage.getItem(ORDER_DRAFT_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as {
          items: OrderItem[];
          orderType: typeof orderType;
          tableNumber: string;
          customer: typeof selectedCustomer;
        };
        setOrderItems(recalculateSubsidies(parsed.items || []));
        setOrderType(parsed.orderType || 'dine-in');
        setTableNumber(parsed.tableNumber || '');
        setSelectedCustomer(parsed.customer || null);
      }
    } catch (error) {
      console.error('Failed to load draft order', error);
    }
  }, [flags.autoClearPosOrderOnLeave]);

  // Persist draft when inputs change
  useEffect(() => {
    if (flags.autoClearPosOrderOnLeave) {
      localStorage.removeItem(ORDER_DRAFT_KEY);
      return;
    }
    persistDraft({
      items: orderItems,
      orderType,
      tableNumber,
      customer: selectedCustomer,
    });
  }, [orderItems, orderType, tableNumber, selectedCustomer, flags.autoClearPosOrderOnLeave, persistDraft]);

  // Handle product click - check if product has modifiers
  const handleProductClick = (product: typeof products[0]) => {
    const hasModifiers = productModifiersCache[product.id];

    if (hasModifiers) {
      // Show modifier selection modal
      setSelectedProductForModifiers(product);
      setModifierModalOpen(true);
    } else {
      // Add directly to order without modifiers
      addToOrder(product, [], 1);
    }
  };

  // Handle modifier selection confirmation
  const handleModifierConfirm = (
    product: { id: string; name: string; price: number },
    modifiers: SelectedModifier[],
    quantity: number
  ) => {
    const fullProduct = products?.find(p => p.id === product.id);
    if (fullProduct) {
      addToOrder(fullProduct, modifiers, quantity);
    }
    setModifierModalOpen(false);
    setSelectedProductForModifiers(null);
  };

  // Helper to calculate total quantity of a product currently in the cart
  const getCartQuantity = (productId: string): number => {
    return orderItems
      .filter(item => item.product_id === productId)
      .reduce((sum, item) => sum + item.quantity, 0);
  };

  const addToOrder = async (
    product: typeof products[0],
    modifiers: SelectedModifier[] = [],
    quantity: number = 1,
    skipStockCheck: boolean = false
  ) => {
    // Check FOH stock for products with inventory tracking (unless skipping for manager override).
    // Composite products always get checked via virtual inventory regardless of track_inventory,
    // since their stock is derived from components.
    const isCompositeProduct = product.product_type === 'composite' || product.product_type === 'composite_variant';
    if (!skipStockCheck && (product.track_inventory || isCompositeProduct)) {
      const fohStock = getFOHStock(product.id);
      const inventoryItem = inventoryItems.find(i => i.product_id === product.id);
      const bohStock = inventoryItem?.quantity_boh ?? 0;
      const cartQuantity = getCartQuantity(product.id);
      const totalRequestedQuantity = cartQuantity + quantity;

      // Check if completely out of stock (FOH = 0)
      if (fohStock <= 0) {
        // Product is completely out of stock - always block
        const stockDescription = isCompositeProduct
          ? `${product.name} is out of stock -- insufficient component inventory to make more.`
          : `${product.name} is currently out of stock (FOH: ${fohStock}, BOH: ${bohStock})`;
        toast.error('Out of Stock', { description: stockDescription });
        return;
      }

      // Check if requested quantity exceeds FOH stock
      if (totalRequestedQuantity > fohStock) {
        const shortfall = totalRequestedQuantity - fohStock;
        const stockLabel = isCompositeProduct ? 'available (from components)' : 'in FOH stock';

        switch (fohStockWarningBehavior) {
          case 'warn_allow':
            // Show warning but allow the sale to proceed
            toast.error('Low Stock Warning', {
              description: `Only ${fohStock} ${product.name} ${stockLabel}. You are adding ${quantity} more (${shortfall} over available stock).`,
            });
            // Continue to add the item (don't return)
            break;

          case 'block_hard':
            // Block completely - don't allow the sale
            toast.error('Insufficient Stock', {
              description: `Cannot add ${quantity} ${product.name}. Only ${Math.max(0, fohStock - cartQuantity)} more ${stockLabel} (${fohStock} total, ${cartQuantity} already in cart).`,
            });
            return;

          case 'require_pin':
          default:
            // Require manager PIN approval
            setPendingStockOverride({
              product,
              modifiers,
              quantity,
              fohStock,
              cartQuantity,
            });
            setStockOverrideDialogOpen(true);
            return;
        }
      }
    }

    const modifierTotal = modifiers.reduce((sum, mod) => sum + mod.price, 0);
    const productPrice = product.effective_price ?? product.price;
    const totalPrice = productPrice + modifierTotal;
    const productIsSubsidy = isSubsidy(productPrice);

    // Create a unique key for items with same product but different modifiers
    const modifierKey = modifiers.length > 0
      ? modifiers.map(m => m.id).sort().join('-')
      : '';

    setOrderItems(prev => {
      // Find existing item with same product AND same modifiers
      const existing = prev.find(item => {
        if (item.product_id !== product.id) return false;
        const itemModifierKey = item.modifiers?.map(m => m.id).sort().join('-') || '';
        return itemModifierKey === modifierKey;
      });

      let newItems: OrderItem[];

      if (existing) {
        newItems = prev.map(item => {
          if (item.id !== existing.id) return item;
          return { ...item, quantity: item.quantity + quantity };
        });
      } else {
        newItems = [
          ...prev,
          {
            id: crypto.randomUUID(),
            product_id: product.id,
            name: product.name,
            price: totalPrice,
            base_price: productPrice,
            effective_price: totalPrice,
            quantity: quantity,
            is_subsidy: productIsSubsidy,
            subsidy_capped: false,
            modifiers: modifiers.length > 0 ? modifiers : undefined,
          },
        ];
      }
      
      // Recalculate subsidies after adding item
      const recalculated = recalculateSubsidies(newItems);
      
      // Show toast if any subsidy was capped
      const cappedSubsidies = recalculated.filter(i => i.subsidy_capped && i.is_subsidy);
      if (cappedSubsidies.length > 0 && productIsSubsidy) {
        const totalUnused = cappedSubsidies.reduce((sum, item) => {
          const originalValue = Math.abs(item.price) * item.quantity;
          const appliedValue = Math.abs(item.effective_price) * item.quantity;
          return sum + (originalValue - appliedValue);
        }, 0);
        
        if (totalUnused > 0) {
          toast.error('⚠️ Subsidy Capped', {
        description: `Unused balance of ${formatCurrency(totalUnused)} will be audited. Subsidy cannot exceed order value.`,
      });
        }
      }
      
      return recalculated;
    });

    // Fetch upsell suggestions for the added product (only for non-subsidy items)
    if (!productIsSubsidy) {
      try {
        // Get current order product IDs to exclude from suggestions
        const currentProductIds = orderItems.map(item => item.product_id);
        currentProductIds.push(product.id); // Include the just-added product

        const suggestions = await getSuggestions(
          product.id,
          currentProductIds,
          products,
          categories,
          3
        );

        if (suggestions.length > 0) {
          setLastAddedProduct({ id: product.id, name: product.name });
          setUpsellSuggestions(suggestions);
        }
      } catch (error) {
        console.error('Error fetching upsell suggestions:', error);
      }
    }
  };

  // Handle adding an upsell suggestion to the order
  const handleAddUpsell = useCallback((suggestion: UpsellSuggestion) => {
    const product = products.find(p => p.id === suggestion.product_id);
    if (product) {
      handleProductClick(product);
    }
    setUpsellSuggestions([]);
    setLastAddedProduct(null);
  }, [products, productModifiersCache]);

  // Handle dismissing upsell popover
  const handleDismissUpsell = useCallback(() => {
    setUpsellSuggestions([]);
    setLastAddedProduct(null);
  }, []);

  // Track upsell shown
  const handleUpsellShown = useCallback((suggestion: UpsellSuggestion) => {
    if (lastAddedProduct) {
      trackShown.mutate({
        sourceProductId: lastAddedProduct.id,
        suggestedProductId: suggestion.product_id,
      });
    }
  }, [lastAddedProduct, trackShown]);

  // Track upsell accepted
  const handleUpsellAccepted = useCallback((suggestion: UpsellSuggestion) => {
    if (lastAddedProduct) {
      trackAccepted.mutate({
        sourceProductId: lastAddedProduct.id,
        suggestedProductId: suggestion.product_id,
      });
    }
  }, [lastAddedProduct, trackAccepted]);

  const updateQuantity = (itemId: string, delta: number) => {
    setOrderItems(prev => {
      const updated = prev
        .map(item =>
          item.id === itemId
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item
        )
        .filter(item => item.quantity > 0);

      // Recalculate subsidies after quantity change
      return recalculateSubsidies(updated);
    });
  };

  const setQuantity = (itemId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeItem(itemId);
      return;
    }
    setOrderItems(prev => {
      const updated = prev.map(item =>
        item.id === itemId
          ? { ...item, quantity: newQuantity }
          : item
      );
      return recalculateSubsidies(updated);
    });
  };

  const handleQuantityEditStart = (itemId: string, currentQuantity: number) => {
    setEditingQuantityId(itemId);
    setEditingQuantityValue(String(currentQuantity));
  };

  const handleQuantityEditConfirm = () => {
    if (editingQuantityId) {
      const newQty = parseInt(editingQuantityValue, 10);
      if (!isNaN(newQty) && newQty > 0) {
        setQuantity(editingQuantityId, newQty);
      }
      setEditingQuantityId(null);
      setEditingQuantityValue('');
    }
  };

  const handleQuantityEditCancel = () => {
    setEditingQuantityId(null);
    setEditingQuantityValue('');
  };

  const removeItem = (itemId: string) => {
    setOrderItems(prev => {
      const updated = prev.filter(item => item.id !== itemId);
      // Recalculate subsidies after removal
      return recalculateSubsidies(updated);
    });
  };

  const clearOrder = () => {
    setOrderItems([]);
    setTableNumber('');
    setSubsidyAuditPending([]);
    setSelectedCustomer(null);
    setCheckoutPayments([]);
    persistDraft(null);
  };

  useEffect(() => {
    try {
      localStorage.setItem(PARKED_SALES_KEY, JSON.stringify(parkedSales));
    } catch (error) {
      console.error('Failed to save parked sales', error);
    }
  }, [parkedSales]);

  // Clear parked sales when register closes
  useEffect(() => {
    if (!isRegisterOpen) {
      setParkedSales([]);
      localStorage.removeItem(PARKED_SALES_KEY);
    }
  }, [isRegisterOpen]);

  // Handle manager approval for stock override
  const handleStockOverrideApproved = (_managerId: string, managerName: string) => {
    if (pendingStockOverride) {
      const { product, modifiers, quantity, fohStock, cartQuantity } = pendingStockOverride;

      // Show confirmation toast
      toast.success('Stock Override Approved', {
        description: `${managerName} approved adding ${quantity} ${product.name} (exceeds FOH stock of ${fohStock} by ${cartQuantity + quantity - fohStock})`,
      });

      // Add the item with skipStockCheck = true
      addToOrder(product, modifiers, quantity, true);
    }

    // Clear the pending override
    setPendingStockOverride(null);
    setStockOverrideDialogOpen(false);
  };

  const handleParkSale = () => {
    if (isTrainingMode) {
      toast.error('Parking Disabled', {
        description: 'Parking sales is disabled in Training Mode.',
      });
      return;
    }
    if (orderItems.length === 0) {
      toast.error('Nothing to Park', {
        description: 'Add items to the order before parking.',
      });
      return;
    }

    const name =
      selectedCustomer?.first_name
        ? `${selectedCustomer.first_name} ${selectedCustomer.last_name ?? ''}`.trim()
        : tableNumber
          ? `Table ${tableNumber}`
          : `Sale ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

    const newParked: ParkedSale = {
      id: crypto.randomUUID(),
      name,
      items: orderItems,
      orderType,
      tableNumber,
      customer: selectedCustomer,
      total,
      createdAt: new Date().toISOString(),
    };

    setParkedSales(prev => [...prev, newParked]);
    clearOrder();
    toast.success('Sale Parked', {
        description: `"${name}" saved. You can resume it later.`,
      });
  };

  const handleResumeParkedSale = (id: string) => {
    const sale = parkedSales.find(p => p.id === id);
    if (!sale) return;

    setOrderItems(recalculateSubsidies(sale.items));
    setOrderType(sale.orderType);
    setTableNumber(sale.tableNumber);
    setSelectedCustomer(sale.customer);
    setParkedSales(prev => prev.filter(p => p.id !== id));
    toast.success('Sale Resumed', {
        description: `"${sale.name}" is back in the cart.`,
      });
  };

  const handleRemoveParkedSale = (id: string) => {
    setParkedSales(prev => prev.filter(p => p.id !== id));
    toast.success('Parked Sale Removed', {
        description: 'The parked sale was deleted.',
      });
    if (parkedSales.length === 1) {
      setParkPopoverOpen(false);
    }
  };

  // Calculate subtotal using effective prices (after subsidy capping)
  const subtotal = orderItems.reduce((sum, item) => sum + item.effective_price * item.quantity, 0);
  // Ensure subtotal never goes below 0
  const cappedSubtotal = Math.max(0, subtotal);

  // Calculate promotion discounts
  const promotionResult: PromotionResult = useMemo(() => {
    if (activePromotions.length === 0 || orderItems.length === 0) {
      return { itemDiscounts: new Map(), totalDiscount: 0, appliedPromotions: [] };
    }
    const cartItems: PromotionCartItem[] = orderItems.map(item => ({
      id: item.id,
      product_id: item.product_id,
      name: item.name,
      price: item.effective_price,
      base_price: item.base_price,
      quantity: item.quantity,
    }));
    return calculatePromotionDiscounts(cartItems, activePromotions);
  }, [orderItems, activePromotions]);

  const promotionDiscount = promotionResult.totalDiscount;
  const subtotalAfterDiscount = Math.max(0, cappedSubtotal - promotionDiscount);
  const tax = subtotalAfterDiscount * 0.15; // 15% GCT (Jamaica)
  const total = subtotalAfterDiscount + tax;
  const cashTenderedValue = useMemo(() => {
    const value = parseFloat(cashTendered || '0');
    return Number.isNaN(value) ? 0 : value;
  }, [cashTendered]);
  const checkoutCollectedTotal = useMemo(
    () => roundCurrency(checkoutPayments.reduce((sum, payment) => sum + payment.amount, 0)),
    [checkoutPayments]
  );
  const paymentBalanceDue = useMemo(
    () => roundCurrency(Math.max(0, total - checkoutCollectedTotal)),
    [checkoutCollectedTotal, total]
  );
  const cashChangeDue = useMemo(
    () => roundCurrency(Math.max(0, cashTenderedValue - paymentBalanceDue)),
    [cashTenderedValue, paymentBalanceDue]
  );
  const balanceAfterCashTender = useMemo(
    () => roundCurrency(Math.max(0, paymentBalanceDue - cashTenderedValue)),
    [cashTenderedValue, paymentBalanceDue]
  );
  const hasPartialCashTender = cashTenderedValue > 0 && balanceAfterCashTender > PAYMENT_AMOUNT_TOLERANCE;
  
  // Check if order has capped subsidies
  const hasCappedSubsidies = orderItems.some(item => item.subsidy_capped && item.is_subsidy);
  
  // Calculate total unused subsidy amount
  const totalUnusedSubsidy = orderItems
    .filter(item => item.is_subsidy && item.subsidy_capped)
    .reduce((sum, item) => {
      const originalValue = Math.abs(item.price) * item.quantity;
      const appliedValue = Math.abs(item.effective_price) * item.quantity;
      return sum + (originalValue - appliedValue);
    }, 0);

  // Log subsidy audits to database
  const logSubsidyAudits = async (orderId: string) => {
    if (subsidyAuditPending.length === 0) return;
    
    const audits = subsidyAuditPending.map(audit => ({
      order_id: orderId,
      product_id: audit.productId,
      user_id: user?.id,
      location_id: currentLocation?.id,
      original_subsidy_amount: audit.originalAmount,
      applied_amount: audit.appliedAmount,
      unused_amount: audit.unusedAmount,
      order_subtotal_before: calculateNonSubsidySubtotal(orderItems),
      subsidy_product_name: audit.productName,
    }));

    try {
      await supabase.from('subsidy_audit_logs').insert(audits);
    } catch (error) {
      console.error('Failed to log subsidy audits:', error);
    }
  };

  // Log receipt print to database
  const logReceiptPrint = async (orderId: string, printType: 'original' | 'reprint', approvedBy?: string) => {
    try {
      await supabase.from('receipt_prints').insert({
        order_id: orderId,
        location_id: currentLocation?.id,
        user_id: user?.id,
        print_type: printType,
        approved_by: approvedBy,
      });
    } catch (error) {
      console.error('Failed to log receipt print:', error);
    }
  };

  const primaryCashType = useMemo(
    () => cashPaymentTypes.find((pt) => pt.is_primary) || cashPaymentTypes[0],
    [cashPaymentTypes]
  );
  const [selectedPaymentOption, setSelectedPaymentOption] = useState<{
    method: PaymentMethod;
    id?: string;
    name?: string;
    isCash: boolean;
  }>({
    method: 'cash',
    isCash: true,
  });

  useEffect(() => {
    if (isCheckoutOpen) {
      setCashTendered(total > 0 ? total.toFixed(2) : '');
      setCheckoutPayments([]);
      setSelectedPaymentOption(
        primaryCashType
          ? { method: 'cash', id: primaryCashType.id, name: primaryCashType.name, isCash: true }
          : { method: 'cash', isCash: true }
      );
    }
  }, [isCheckoutOpen, total, primaryCashType]);

  const handleSendToKitchen = async () => {
    if (orderItems.length === 0 || !currentLocation) return;

    // Training mode: simulate kitchen send without DB writes
    if (isTrainingMode) {
      const orderNumber = getNextOrderNumber();
      addTrainingOrder({
        id: crypto.randomUUID(),
        order_number: orderNumber,
        order_type: orderType,
        status: 'sent',
        subtotal: cappedSubtotal,
        tax_amount: tax,
        discount_amount: promotionDiscount,
        tip_amount: 0,
        total,
        created_at: new Date().toISOString(),
        completed_at: null,
        items: orderItems.map(item => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.effective_price,
          total: item.effective_price * item.quantity,
        })),
      });
      clearOrder();
      toast.success('Order Sent to Kitchen (Training)', {
        description: `Training order ${orderNumber} -- ${orderItems.length} item${orderItems.length > 1 ? 's' : ''} sent (not recorded)`,
      });
      return;
    }

    setIsSending(true);
    try {
      // Convert order type format
      const orderTypeMap: Record<string, 'dine_in' | 'takeout' | 'delivery' | 'bar_tab'> = {
        'dine-in': 'dine_in',
        'takeout': 'takeout',
        'delivery': 'delivery',
      };

      const result = await createOrderOffline.mutateAsync({
        order_type: orderTypeMap[orderType],
        table_number: tableNumber || undefined,
        customer_id: selectedCustomer?.id,
        items: orderItems.map(item => ({
          product_id: item.product_id,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.base_price ?? item.effective_price, // Use base price, modifiers added separately
          notes: item.notes,
          modifiers: item.modifiers?.map(m => ({ id: m.id, name: m.name, price: m.price })),
        })),
      });

      // Log subsidy audits if any
      if (result?.order?.id && subsidyAuditPending.length > 0) {
        await logSubsidyAudits(result.order.id);
      }

      // Clear the order after successful send
      clearOrder();

      if (result.isOffline) {
        toast('Order Saved (Offline)', {
          description: `Order saved locally. Will sync when connection is restored.`,
        });
      } else {
        toast.success('Order Sent to Kitchen', {
          description: `${orderItems.length} item${orderItems.length > 1 ? 's' : ''} sent successfully`,
        });
      }
    } catch (error) {
      console.error('Failed to send order:', error);
      toast.error('Failed to Send Order', {
        description: error instanceof Error ? error.message : 'Please try again',
      });
    } finally {
      setIsSending(false);
    }
  };

  // Handle payment and automatically send to kitchen
  // Handle payment with custom payment type
  const handlePaymentWithType = async (
    paymentMethod: PaymentMethod,
    paymentTypeId?: string,
    paymentTypeName?: string,
    tenderedAmount?: number
  ) => {
    if (orderItems.length === 0 || !currentLocation) return;

    // Training mode: simulate payment without any DB writes
    if (isTrainingMode) {
      const orderNumber = getNextOrderNumber();
      const orderTypeMap: Record<string, string> = {
        'dine-in': 'dine_in',
        'takeout': 'takeout',
        'delivery': 'delivery',
      };
      const isCashPayment = paymentMethod === 'cash';
      const changeDue =
        isCashPayment && typeof tenderedAmount === 'number'
          ? Math.max(0, tenderedAmount - total)
          : 0;

      const trainingOrder = {
        id: crypto.randomUUID(),
        order_number: orderNumber,
        order_type: orderTypeMap[orderType] || orderType,
        status: 'completed',
        subtotal: cappedSubtotal,
        tax_amount: tax,
        discount_amount: promotionDiscount,
        tip_amount: 0,
        total,
        created_at: new Date().toISOString(),
        completed_at: new Date().toISOString(),
        items: orderItems.map(item => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.effective_price,
          total: item.effective_price * item.quantity,
        })),
      };
      addTrainingOrder(trainingOrder);

      // Build receipt data for training mode printing
      const receiptOrder = {
        id: trainingOrder.id,
        order_number: orderNumber,
        order_type: orderTypeMap[orderType] || orderType,
        status: 'completed',
        subtotal: cappedSubtotal,
        tax_amount: tax,
        discount_amount: promotionDiscount,
        tip_amount: 0,
        total,
        notes: undefined,
        guest_count: 0,
        created_at: trainingOrder.created_at,
        completed_at: trainingOrder.completed_at,
        table: tableNumber ? { name: tableNumber } : null,
        customer: selectedCustomer
          ? { first_name: selectedCustomer.first_name, last_name: selectedCustomer.last_name }
          : null,
        server: user
          ? { first_name: user.user_metadata?.first_name || user.email || '', last_name: user.user_metadata?.last_name || '' }
          : null,
        items: orderItems.map(item => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.effective_price,
          total: item.effective_price * item.quantity,
          notes: item.notes || null,
          modifiers: [],
          status: 'completed',
        })),
      };

      const locationInfo = currentLocation
        ? {
            name: currentLocation.name,
            address: currentLocation.address
              ? { street: currentLocation.address.line1, city: currentLocation.address.city, parish: currentLocation.address.parish }
              : undefined,
            phone: currentLocation.phone || undefined,
          }
        : undefined;

      // Print training receipt (with training mode label)
      if (shouldAutoPrint) {
        printReceipt({
          order: receiptOrder,
          location: locationInfo,
          isReprint: false,
          isTrainingMode: true,
        });
      }

      setIsCheckoutOpen(false);
      setCashTendered('');
      setCheckoutPayments([]);
      clearOrder();

      // Play success sound
      try {
        const audio = new Audio(getAssetPath('/sounds/payment-success.mp3'));
        audio.volume = 0.5;
        audio.play().catch(() => {});
      } catch {
        // Sound not available
      }

      toast.success('Payment Complete (Training)', {
        description: isCashPayment
          ? `Training order ${orderNumber} paid in cash (${formatCurrency(tenderedAmount ?? total)} tendered). Change ${formatCurrency(changeDue)}. Not recorded.`
          : `Training order ${orderNumber} paid via ${paymentTypeName || paymentMethod}. Not recorded.`,
      });
      return;
    }

    setIsProcessingPayment(true);
    try {
      // Convert order type format
      const orderTypeMap: Record<string, 'dine_in' | 'takeout' | 'delivery' | 'bar_tab'> = {
        'dine-in': 'dine_in',
        'takeout': 'takeout',
        'delivery': 'delivery',
      };

      // Step 1: Create order (with offline fallback support)
      const result = await createOrderOffline.mutateAsync({
        order_type: orderTypeMap[orderType],
        table_number: tableNumber || undefined,
        customer_id: selectedCustomer?.id,
        discount_amount: promotionDiscount > 0 ? promotionDiscount : undefined,
        items: orderItems.map(item => {
          const itemPromo = promotionResult.itemDiscounts.get(item.id);
          return {
            product_id: item.product_id,
            name: item.name,
            quantity: item.quantity,
            unit_price: item.base_price ?? item.effective_price, // Use base price, modifiers added separately
            notes: item.notes,
            modifiers: item.modifiers?.map(m => ({ id: m.id, name: m.name, price: m.price })),
            promotion_id: itemPromo?.promotionId,
            promotion_discount: itemPromo?.discount,
          };
        }),
      });

      if (!result?.order?.id) {
        throw new Error('Failed to create order');
      }

      const orderId = result.order.id;
      const isOfflineOrder = result.isOffline;
      const isCashPayment = paymentMethod === 'cash';
      const changeDue =
        isCashPayment && typeof tenderedAmount === 'number'
          ? Math.max(0, tenderedAmount - total)
          : 0;

      // Log subsidy audits if any
      if (subsidyAuditPending.length > 0) {
        await logSubsidyAudits(orderId);
      }

      // Step 2: Process payment
      await addPayment.mutateAsync({
        orderId,
        paymentMethod,
        amount: total,
        tipAmount: 0,
        paymentTypeId,
      });

      // Step 3: Complete the order (mark as paid)
      await completeOrder.mutateAsync({ orderId });

      setIsCheckoutOpen(false);
      setCashTendered('');
      setCheckoutPayments([]);

      // Prepare receipt data before clearing local state
      const receiptOrder = {
        id: orderId,
        order_number: result.order.order_number || orderId.slice(0, 6),
        order_type: orderTypeMap[orderType],
        status: 'completed',
        subtotal: cappedSubtotal,
        tax_amount: tax,
        discount_amount: promotionDiscount,
        tip_amount: 0,
        total,
        notes: undefined,
        guest_count: 0,
        created_at: result.order.created_at || new Date().toISOString(),
        completed_at: new Date().toISOString(),
        table: tableNumber ? { name: tableNumber } : null,
        customer: selectedCustomer
          ? {
              first_name: selectedCustomer.first_name,
              last_name: selectedCustomer.last_name,
            }
          : null,
        server: user
          ? {
              first_name: user.user_metadata?.first_name || user.email || '',
              last_name: user.user_metadata?.last_name || '',
            }
          : null,
        items: orderItems.map(item => {
          const itemPromo = promotionResult.itemDiscounts.get(item.id);
          return {
            id: item.id,
            name: item.name,
            quantity: item.quantity,
            unit_price: item.effective_price,
            total: item.effective_price * item.quantity - (itemPromo?.discount || 0),
            notes: item.notes || null,
            modifiers: [],
            status: 'completed',
            promotion_name: itemPromo?.promotionName || null,
            promotion_discount: itemPromo?.discount || 0,
          };
        }),
      };

      const locationInfo = currentLocation
        ? {
            name: currentLocation.name,
            address: currentLocation.address
              ? {
                  street: currentLocation.address.line1,
                  city: currentLocation.address.city,
                  parish: currentLocation.address.parish,
                }
              : undefined,
            phone: currentLocation.phone || undefined,
          }
        : undefined;

      // Auto print receipt on completion
      await logReceiptPrint(orderId, 'original');
      if (shouldAutoPrint) {
        printReceipt({
          order: receiptOrder,
          location: locationInfo,
          isReprint: false,
        });
      }

      // Clear the order
      clearOrder();

      // Play success sound if available
      try {
        const audio = new Audio(getAssetPath('/sounds/payment-success.mp3'));
        audio.volume = 0.5;
        audio.play().catch(() => {});
      } catch {
        // Sound not available, ignore
      }

      if (isOfflineOrder) {
        toast('Order Saved (Offline)', {
          description: `Order #${result.order.order_number} saved locally. Will sync when connection is restored.`,
        });
      } else {
        toast.success('Payment Complete', {
          description: isCashPayment
            ? `Order #${result.order.order_number} paid in cash (${formatCurrency(tenderedAmount ?? total)} tendered). Change ${formatCurrency(changeDue)}. Sent to kitchen.`
            : `Order #${result.order.order_number} paid via ${paymentTypeName || paymentMethod}. Sent to kitchen.`,
        });
      }
    } catch (error) {
      console.error('Failed to process payment:', error);
      toast.error('Payment Failed', {
        description: error instanceof Error ? error.message : 'Please try again',
      });
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleCashPayment = async (paymentTypeId?: string, paymentTypeName?: string) => {
    const tendered = parseFloat(cashTendered || '0');

    if (Number.isNaN(tendered) || tendered <= 0) {
      toast.error('Invalid Amount', {
        description: 'Please enter how much cash was tendered.',
      });
      return;
    }

    if (paymentBalanceDue <= PAYMENT_AMOUNT_TOLERANCE) {
      toast.error('No Balance Due', {
        description: 'This sale is already fully paid.',
      });
      return;
    }

    const amountApplied = roundCurrency(Math.min(tendered, paymentBalanceDue));
    const nextPayments: CheckoutPayment[] = [
      ...checkoutPayments,
      {
        id: crypto.randomUUID(),
        paymentTypeId: paymentTypeId || '_default_cash',
        paymentTypeName: paymentTypeName || 'Cash',
        paymentMethod: 'cash',
        amount: amountApplied,
        cashTendered: tendered,
      },
    ];
    const nextBalanceDue = roundCurrency(Math.max(0, paymentBalanceDue - amountApplied));

    if (nextBalanceDue > PAYMENT_AMOUNT_TOLERANCE) {
      setCheckoutPayments(nextPayments);
      setCashTendered(nextBalanceDue.toFixed(2));
      toast.success('Cash Collected', {
        description: `${formatCurrencyCents(amountApplied)} cash applied. ${formatCurrencyCents(nextBalanceDue)} still due.`,
      });
      return;
    }

    if (checkoutPayments.length === 0) {
      return handlePaymentWithType('cash', paymentTypeId, paymentTypeName, tendered);
    }

    try {
      await handleSplitPayment(nextPayments);
    } catch {
      // handleSplitPayment already shows the relevant payment error.
    }
  };

  // Handle split payment across multiple payment methods
  const handleSplitPayment = async (
    payments: Array<{
      paymentTypeId: string;
      paymentMethod: CheckoutPaymentMethod;
      amount: number;
      cashTendered?: number;
    }>
  ) => {
    if (orderItems.length === 0 || !currentLocation) return;

    const paymentTotal = roundCurrency(payments.reduce((sum, payment) => sum + payment.amount, 0));
    if (Math.abs(paymentTotal - total) > PAYMENT_AMOUNT_TOLERANCE) {
      const message = `Payments must equal the order total. Remaining balance: ${formatCurrencyCents(Math.max(0, total - paymentTotal))}.`;
      toast.error('Payment Total Mismatch', {
        description: message,
      });
      throw new Error(message);
    }

    // Training mode: simulate split payment without DB writes
    if (isTrainingMode) {
      const orderNumber = getNextOrderNumber();
      addTrainingOrder({
        id: crypto.randomUUID(),
        order_number: orderNumber,
        order_type: orderType,
        status: 'completed',
        subtotal: cappedSubtotal,
        tax_amount: tax,
        discount_amount: promotionDiscount,
        tip_amount: 0,
        total,
        created_at: new Date().toISOString(),
        completed_at: new Date().toISOString(),
        items: orderItems.map(item => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.effective_price,
          total: item.effective_price * item.quantity,
        })),
      });

      setIsCheckoutOpen(false);
      setIsSplitPaymentOpen(false);
      setCashTendered('');
      setCheckoutPayments([]);
      clearOrder();

      toast.success('Split Payment Complete (Training)', {
        description: `Training order ${orderNumber} paid with ${payments.length} payment methods. Not recorded.`,
      });
      return;
    }

    setIsProcessingPayment(true);

    try {
      // Convert order type format
      const splitOrderTypeMap: Record<string, 'dine_in' | 'takeout' | 'delivery' | 'bar_tab'> = {
        'dine-in': 'dine_in',
        'takeout': 'takeout',
        'delivery': 'delivery',
      };

      // Step 1: Create order
      const result = await createOrderOffline.mutateAsync({
        order_type: splitOrderTypeMap[orderType],
        table_number: tableNumber || undefined,
        customer_id: selectedCustomer?.id,
        items: orderItems.map((item) => ({
          product_id: item.product_id,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.base_price ?? item.effective_price,
          notes: item.notes,
          modifiers: item.modifiers?.map(m => ({ id: m.id, name: m.name, price: m.price })),
        })),
      });

      if (!result?.order?.id) throw new Error('Failed to create order');

      const orderId = result.order.id;

      // Log subsidy audits if any
      if (subsidyAuditPending.length > 0) {
        await logSubsidyAudits(orderId);
      }

      // Step 2: Process each payment
      for (const payment of payments) {
        // Convert _default_ prefixed IDs to undefined (these are built-in types without DB records)
        const resolvedPaymentTypeId = payment.paymentTypeId?.startsWith('_default_')
          ? undefined
          : payment.paymentTypeId || undefined;

        await addPayment.mutateAsync({
          orderId,
          paymentMethod: payment.paymentMethod,
          amount: payment.amount,
          tipAmount: 0,
          paymentTypeId: resolvedPaymentTypeId,
        });

      }

      // Step 3: Complete the order
      await completeOrder.mutateAsync({ orderId });

      setIsCheckoutOpen(false);
      setIsSplitPaymentOpen(false);
      setCashTendered('');
      setCheckoutPayments([]);

      // Print receipt
      const receiptOrder = {
        id: orderId,
        order_number: result.order.order_number || orderId.slice(0, 6),
        order_type: splitOrderTypeMap[orderType],
        status: 'completed',
        subtotal: cappedSubtotal,
        tax_amount: tax,
        discount_amount: promotionDiscount,
        tip_amount: 0,
        total,
        notes: undefined,
        guest_count: 0,
        created_at: result.order.created_at || new Date().toISOString(),
        completed_at: new Date().toISOString(),
        table: tableNumber ? { name: tableNumber } : null,
        customer: selectedCustomer
          ? {
              first_name: selectedCustomer.first_name,
              last_name: selectedCustomer.last_name,
            }
          : null,
        server: user
          ? {
              first_name: user.user_metadata?.first_name || user.email || '',
              last_name: user.user_metadata?.last_name || '',
            }
          : null,
        items: orderItems.map(item => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.effective_price,
          total: item.effective_price * item.quantity,
          notes: item.notes || null,
          modifiers: [],
          status: 'completed',
        })),
      };

      const locationInfo = currentLocation
        ? {
            name: currentLocation.name,
            address: (currentLocation as any).address,
            phone: (currentLocation as any).phone,
          }
        : undefined;

      // Print receipt
      if (printReceipt) {
        printReceipt({
          order: receiptOrder,
          location: locationInfo,
          isReprint: false,
        });
      }

      clearOrder();

      // Calculate total change due from cash payments
      const totalChangeDue = payments.reduce((sum, p) => {
        if (p.cashTendered && p.cashTendered > p.amount) {
          return sum + (p.cashTendered - p.amount);
        }
        return sum;
      }, 0);

      toast.success('Split Payment Complete', {
        description: totalChangeDue > 0
          ? `Order #${result.order.order_number} paid. Change due: ${formatCurrency(totalChangeDue)}`
          : `Order #${result.order.order_number} paid with ${payments.length} payment methods`,
      });
    } catch (error: any) {
      console.error('Split payment error:', error);
      toast.error('Payment Failed', {
        description: error.message || 'Failed to process split payment',
      });
      throw error;
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleNonCashPayment = async (
    paymentMethod: 'card' | 'gift_card' | 'store_credit' | 'other',
    paymentTypeId?: string,
    paymentTypeName?: string
  ) => {
    if (checkoutPayments.length === 0 && hasPartialCashTender) {
      toast.error('Cash Not Collected', {
        description: 'Click Collect Cash before using another payment method for the remaining balance.',
      });
      return;
    }

    if (checkoutPayments.length === 0) {
      return handlePaymentWithType(paymentMethod, paymentTypeId, paymentTypeName);
    }

    if (paymentBalanceDue <= PAYMENT_AMOUNT_TOLERANCE) {
      toast.error('No Balance Due', {
        description: 'This sale is already fully paid.',
      });
      return;
    }

    try {
      await handleSplitPayment([
        ...checkoutPayments,
        {
          paymentTypeId: paymentTypeId || (paymentMethod === 'card' ? '_default_card' : ''),
          paymentMethod,
          amount: paymentBalanceDue,
        },
      ]);
    } catch {
      // handleSplitPayment already shows the relevant payment error.
    }
  };

  // Auto-create virtual session for admins/managers entering POS without a register
  useEffect(() => {
    const initVirtualSession = async () => {
      // Only create virtual session for admins/managers who don't have any active session
      if (canUseVirtualSession && !activeSession && !isCashier && currentLocation?.id) {
        try {
          await createVirtualSession.mutateAsync();
        } catch (error) {
          console.error('Failed to create virtual session:', error);
        }
      }
    };
    
    if (!virtualSessionLoading && !registerLoading) {
      initVirtualSession();
    }
  }, [canUseVirtualSession, activeSession, isCashier, currentLocation?.id, virtualSessionLoading, registerLoading]);

  // Show register requirement for cashiers without open register
  // Admins/managers can use POS with virtual sessions - they don't need a physical register
  if (!registerLoading && !virtualSessionLoading && isCashier && !isRegisterOpen) {
    return (
      <DashboardLayout
        header={
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-2xl font-display font-semibold tracking-tight">
              Sell
            </h1>
          </div>
        }
      >
        <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)]">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
                <AlertTriangle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
              </div>
              <CardTitle>Register Not Open</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-muted-foreground">
                You must open a register before you can start selling. Please go to Open / Close to
                open a register and start your shift.
              </p>
              <Button onClick={() => navigate('/sell/open-close')} className="w-full">
                Open Register
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Show location selection prompt when no location is selected
  if (!currentLocation) {
    return (
      <DashboardLayout
        header={
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-2xl font-display font-semibold tracking-tight">
              Sell
            </h1>
          </div>
        }
      >
        <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)]">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                <Users className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <CardTitle>Select a Location</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-muted-foreground">
                Please select a location from the sidebar to start selling.
                Each location has its own menu, pricing, and settings.
              </p>
              <p className="text-xs text-muted-foreground">
                Use the location selector in the navigation bar to choose your working location.
              </p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const header = (
    <div className="flex items-center justify-between gap-4">
      <div>
        <h1 className="text-2xl font-display font-semibold tracking-tight">
          Point of Sale
        </h1>
        <p className="text-sm text-muted-foreground">
          {currentLocation?.name || 'Select a location'} • Create and manage orders
        </p>
      </div>
      <div className="flex items-center gap-2">
        <LocationSelector showAllOption={false} />
        {/* Training Mode switch button */}
        <TrainingModeSwitch
          hasItemsInCart={orderItems.length > 0}
          onClearOrder={clearOrder}
        />
        {/* Support request button for cashiers and employees only */}
        {(effectiveRole === 'cashier' || effectiveRole === 'employee') && (
          <RequestSupportCompact
            hasPendingRequest={hasPendingRequest}
            pendingRequest={myPendingRequest || null}
            onCreateRequest={(data) => createSupportRequest.mutateAsync(data)}
            onCancelRequest={(id) => cancelSupportRequest.mutateAsync(id)}
            isCreating={createSupportRequest.isPending}
            isCancelling={cancelSupportRequest.isPending}
          />
        )}
        {/* Virtual session indicator for admins/managers */}
        {isVirtualSession && (
          <Badge variant="secondary" className="gap-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20">
            <Receipt className="h-3 w-3" />
            Virtual Session
          </Badge>
        )}
        <Badge variant="outline" className="gap-1">
          <Clock className="h-3 w-3" />
          {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </Badge>
      </div>
    </div>
  );

  return (
    <DashboardLayout header={header}>
      {/* Training Mode Banner */}
      <TrainingModeBanner />

      {/* Training Mode Tutorial Overlay */}
      <TrainingModeTutorial />

      {/* Offline Mode Indicator */}
      {!isOnline && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-amber-500/10 border border-amber-500/20 px-4 py-2 text-amber-600 dark:text-amber-400">
          <WifiOff className="h-4 w-4" />
          <span className="text-sm font-medium">Offline Mode - Orders will sync when connection is restored</span>
        </div>
      )}
      <div ref={posAreaRef} className="flex gap-6 h-[calc(100vh-140px)]">
        {/* Left: Menu Items */}
        <div className={cn('flex-1 flex flex-col min-w-0', compactPOS && 'hidden')}>
          {/* Search & Filters */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search menu items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center rounded-lg border p-1">
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setViewMode('grid')}
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Categories */}
          <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
            <Button
              variant={selectedCategory === null ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(null)}
              className="shrink-0"
            >
              All Items
            </Button>
            {categories?.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(category.id)}
                className="shrink-0"
              >
                {category.name}
              </Button>
            ))}
          </div>

          {/* Products Grid/List */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <img src="/blazepos-logo.png" alt="" className="h-5 w-5 animate-pulse" />
                  <span>Loading menu...</span>
                </div>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <ChefHat className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="font-medium">No items found</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {searchQuery ? 'Try a different search term' : 'Add items to your menu'}
                </p>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid [grid-template-columns:repeat(auto-fill,minmax(150px,1fr))] gap-3">
                {filteredProducts.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => handleProductClick(product)}
                    className={cn(
                      'group relative flex flex-col rounded-xl border bg-card p-3 text-left transition-all',
                      'hover:border-primary/50 hover:shadow-md hover:shadow-primary/5',
                      'active:scale-[0.98]',
                      !product.is_available && 'opacity-50 pointer-events-none'
                    )}
                  >
                    {product.image_url ? (
                      <div className="aspect-square w-full rounded-lg bg-muted mb-2 overflow-hidden">
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="aspect-square w-full rounded-lg bg-gradient-to-br from-blaze-100/50 to-blaze-50/50 dark:from-blaze-950/50 dark:to-blaze-900/30 mb-2 flex items-center justify-center">
                        <ChefHat className="h-8 w-8 text-blaze-500/50" />
                      </div>
                    )}
                    <h4 className="font-medium text-sm line-clamp-1">{product.name}</h4>
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                      {product.description || 'No description'}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="font-semibold text-primary">
                        {formatCurrency(product.effective_price ?? product.price)}
                      </span>
                      <div className="flex items-center gap-1">
                        {productModifiersCache[product.id] && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 gap-0.5">
                            <Settings2 className="h-2.5 w-2.5" />
                            Customize
                          </Badge>
                        )}
                        {!product.is_available && (
                          <Badge variant="secondary" className="text-xs">86'd</Badge>
                        )}
                      </div>
                    </div>
                    <div className="absolute inset-0 rounded-xl ring-2 ring-primary ring-offset-2 opacity-0 group-focus:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredProducts.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => handleProductClick(product)}
                    className={cn(
                      'w-full flex items-center gap-3 rounded-xl border bg-card p-3 text-left transition-all',
                      'hover:border-primary/50 hover:shadow-md',
                      'active:scale-[0.99]',
                      !product.is_available && 'opacity-50 pointer-events-none'
                    )}
                  >
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blaze-100/50 to-blaze-50/50 dark:from-blaze-950/50 dark:to-blaze-900/30 flex items-center justify-center shrink-0">
                      <ChefHat className="h-5 w-5 text-blaze-500/50" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <h4 className="font-medium text-sm">{product.name}</h4>
                        {productModifiersCache[product.id] && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 gap-0.5">
                            <Settings2 className="h-2.5 w-2.5" />
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {product.description || 'No description'}
                      </p>
                    </div>
                    <span className="font-semibold text-primary shrink-0">
                      {formatCurrency(product.effective_price ?? product.price)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: Order Panel */}
        <Card className={cn('shrink-0 flex flex-col h-full', compactPOS ? 'w-full' : 'w-96')}>
          <CardHeader className="pb-3">
              {/* Compact mode: product search replaces the hidden grid */}
              {compactPOS && (
                <div className="mb-3 space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search for products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  {searchQuery.trim() !== '' && (
                    <div className="max-h-72 overflow-y-auto rounded-lg border divide-y divide-border">
                      {filteredProducts.length === 0 ? (
                        <p className="p-3 text-sm text-muted-foreground">No products found</p>
                      ) : (
                        filteredProducts.slice(0, 50).map((product) => (
                          <button
                            key={product.id}
                            onClick={() => handleProductClick(product)}
                            className={cn(
                              'flex w-full items-center justify-between gap-3 p-2.5 text-left transition-colors hover:bg-muted/70',
                              !product.is_available && 'opacity-50 pointer-events-none'
                            )}
                          >
                            <span className="min-w-0 truncate text-sm font-medium">{product.name}</span>
                            <span className="shrink-0 text-sm font-semibold text-primary">
                              {formatCurrency(product.effective_price ?? product.price)}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-lg">Current Order</CardTitle>
                <div className="flex items-center gap-2">
                  <Popover open={parkPopoverOpen} onOpenChange={setParkPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1"
                      >
                        <PauseCircle className="h-4 w-4" />
                        Parked ({parkedSales.length})
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold">Parked Sales</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={parkedSales.length === 0}
                          onClick={() => setParkedSales([])}
                        >
                          Clear All
                        </Button>
                      </div>
                      {parkedSales.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No parked sales.</p>
                      ) : (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {parkedSales.map((sale) => (
                            <div
                              key={sale.id}
                              className="flex items-center gap-2 rounded-md border bg-muted/40 p-2"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{sale.name}</p>
                                <p className="text-[11px] text-muted-foreground">
                                  {formatCurrencyCents(sale.total)} • {new Date(sale.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="secondary"
                                className="gap-1"
                                onClick={() => {
                                  handleResumeParkedSale(sale.id);
                                  setParkPopoverOpen(false);
                                }}
                              >
                                <PlayCircle className="h-4 w-4" />
                                Resume
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleRemoveParkedSale(sale.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                  {orderItems.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={clearOrder}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            
            {/* Order Type Selector */}
            <div className="flex gap-2 mt-2">
              {(['dine-in', 'takeout', 'delivery'] as const).map((type) => (
                <Button
                  key={type}
                  variant={orderType === type ? 'default' : 'outline'}
                  size="sm"
                  className="flex-1 capitalize"
                  onClick={() => setOrderType(type)}
                >
                  {type}
                </Button>
              ))}
            </div>

            {orderType === 'dine-in' && (
              <div className="mt-2 flex gap-2">
                <Input
                  placeholder="Table #"
                  value={tableNumber}
                  onChange={(e) => setTableNumber(e.target.value)}
                  className="text-center font-medium w-24"
                />
                <Button
                  variant="outline"
                  className="flex-1 gap-2"
                  onClick={() => setShowCustomerModal(true)}
                >
                  <Users className="h-4 w-4" />
                  {selectedCustomer ? (
                    <span className="truncate">
                      {selectedCustomer.first_name} {selectedCustomer.last_name}
                    </span>
                  ) : (
                    'Select Customer'
                  )}
                </Button>
              </div>
            )}

            {orderType !== 'dine-in' && (
              <div className="mt-2">
                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={() => setShowCustomerModal(true)}
                >
                  <Users className="h-4 w-4" />
                  {selectedCustomer ? (
                    <span className="truncate">
                      {selectedCustomer.first_name} {selectedCustomer.last_name}
                    </span>
                  ) : (
                    'Select Customer'
                  )}
                </Button>
              </div>
            )}
          </CardHeader>

          <CardContent className="flex-1 flex flex-col overflow-hidden">
            {/* Order Items */}
            <div className="flex-1 overflow-y-auto -mx-4 px-4">
              {orderItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-8">
                  <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-3">
                    <Receipt className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Tap items to add to order
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {orderItems.map((item) => (
                    <div
                      key={item.id}
                      className={cn(
                        "rounded-lg border p-2 space-y-1.5",
                        item.is_subsidy
                          ? "bg-green-500/10 border-green-500/30"
                          : "bg-card/50",
                        item.subsidy_capped && "border-amber-500/50 bg-amber-500/10"
                      )}
                    >
                      {/* Name on its own full-width row so it is never squeezed/
                          truncated by the quantity controls and price. */}
                      <div className="flex items-start gap-1.5">
                        <p className="font-medium text-sm flex-1 min-w-0 break-words">{item.name}</p>
                        {item.modifiers && item.modifiers.length > 0 && (
                          <Settings2 className="h-3 w-3 text-primary shrink-0 mt-0.5" />
                        )}
                        {item.is_subsidy && (
                          <Tag className="h-3 w-3 text-green-600 shrink-0 mt-0.5" />
                        )}
                        {item.subsidy_capped && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-amber-500/20 text-amber-700 border-amber-500/50 shrink-0">
                            CAPPED
                          </Badge>
                        )}
                      </div>
                      {item.modifiers && item.modifiers.length > 0 && (
                        <p className="text-[11px] text-primary/80 truncate">
                          {item.modifiers.map(m => m.name).join(', ')}
                        </p>
                      )}

                      {/* Controls row: per-unit price, quantity stepper, line total, remove. */}
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-muted-foreground flex-1 min-w-0 truncate">
                          {item.subsidy_capped && item.is_subsidy ? (
                            <span className="text-amber-600">
                              {formatCurrency(item.effective_price)} applied (was {formatCurrency(item.price)})
                            </span>
                          ) : (
                            <span>{formatCurrency(item.price)} each</span>
                          )}
                        </p>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => updateQuantity(item.id, -1)}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          {editingQuantityId === item.id ? (
                            <Input
                              type="number"
                              inputMode="numeric"
                              value={editingQuantityValue}
                              onChange={(e) => setEditingQuantityValue(e.target.value)}
                              onBlur={handleQuantityEditConfirm}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleQuantityEditConfirm();
                                if (e.key === 'Escape') handleQuantityEditCancel();
                              }}
                              className="w-14 h-7 text-center text-sm p-1"
                              autoFocus
                              min={1}
                            />
                          ) : (
                            <span
                              className="w-8 text-center font-medium text-sm cursor-pointer hover:bg-muted rounded px-1"
                              onDoubleClick={() => handleQuantityEditStart(item.id, item.quantity)}
                              title="Double-click to edit"
                            >
                              {item.quantity}
                            </span>
                          )}
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => updateQuantity(item.id, 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="w-20 text-right shrink-0">
                          <p className={cn(
                            "font-semibold text-sm",
                            item.is_subsidy && "text-green-600"
                          )}>
                            {formatCurrency(item.effective_price * item.quantity)}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0"
                          onClick={() => removeItem(item.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upsell Suggestions Popover */}
            {upsellSuggestions.length > 0 && lastAddedProduct && (
              <div className="relative mt-2">
                <UpsellPopover
                  suggestions={upsellSuggestions}
                  sourceProductId={lastAddedProduct.id}
                  sourceProductName={lastAddedProduct.name}
                  onAddToOrder={handleAddUpsell}
                  onDismiss={handleDismissUpsell}
                  onShown={handleUpsellShown}
                  onAccepted={handleUpsellAccepted}
                  autoHideDelay={8000}
                  compact={!flags.expandedUpsellPopover}
                />
              </div>
            )}

            {/* Totals & Actions */}
            {orderItems.length > 0 && (
              <div className="border-t pt-4 mt-4 space-y-3">
                {/* Subsidy Audit Warning */}
                {hasCappedSubsidies && totalUnusedSubsidy > 0 && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-xs">
                      <p className="font-medium text-amber-700">Subsidy Capped</p>
                      <p className="text-amber-600 mt-0.5">
                        Unused balance of {formatCurrency(totalUnusedSubsidy)} will be audited. 
                        Subsidy cannot exceed order value.
                      </p>
                    </div>
                  </div>
                )}
                
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{formatCurrency(cappedSubtotal)}</span>
                  </div>
                  {hasCappedSubsidies && (
                    <div className="flex justify-between text-amber-600">
                      <span className="text-xs">Unused subsidy (audited)</span>
                      <span className="text-xs">{formatCurrency(totalUnusedSubsidy)}</span>
                    </div>
                  )}
                  {promotionDiscount > 0 && (
                    <div className="space-y-0.5">
                      {promotionResult.appliedPromotions.map(ap => (
                        <div key={ap.id} className="flex justify-between text-green-600">
                          <span className="text-xs flex items-center gap-1">
                            <Tag className="h-3 w-3" />
                            {ap.name}
                          </span>
                          <span className="text-xs">-{formatCurrency(ap.totalDiscount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tax (15%)</span>
                    <span>{formatCurrency(tax)}</span>
                  </div>
                  <div className="flex justify-between text-base font-semibold pt-2 border-t">
                    <span>Total</span>
                    <span className={cn(
                      total === 0 && hasCappedSubsidies ? "text-green-600" : "text-primary"
                    )}>
                      {formatCurrency(total)}
                    </span>
                  </div>
                </div>

                {/* Send to Kitchen - only visible to admin roles */}
                {canSendToKitchen && (
                  <Button
                    variant="outline"
                    className="w-full gap-2"
                    onClick={handleSendToKitchen}
                    disabled={isSending || orderItems.length === 0}
                  >
                    {isSending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ChefHat className="h-4 w-4" />
                    )}
                    {isSending ? 'Sending...' : 'Send to Kitchen'}
                  </Button>
                )}

                <Button
                  variant="blaze"
                  className="w-full gap-2"
                  onClick={() => setIsCheckoutOpen(true)}
                  disabled={isProcessingPayment || orderItems.length === 0}
                >
                  {isProcessingPayment ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Receipt className="h-4 w-4" />
                  )}
                  Checkout
                </Button>

                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={handleParkSale}
                  disabled={orderItems.length === 0 || isTrainingMode}
                >
                  <PauseCircle className="h-4 w-4" />
                  Park Sale
                </Button>

              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Checkout Modal - Lightspeed-inspired */}
      <Dialog
        open={isCheckoutOpen}
        onOpenChange={(open) => {
          if (!open && checkoutPayments.length > 0 && paymentBalanceDue > PAYMENT_AMOUNT_TOLERANCE) {
            toast.error('Payment In Progress', {
              description: `${formatCurrencyCents(paymentBalanceDue)} is still due before this sale can be completed.`,
            });
            return;
          }
          setIsCheckoutOpen(open);
          if (!open) {
            setCashTendered('');
            setCheckoutPayments([]);
          }
        }}
      >
        <DialogContent className="max-w-4xl" hideCloseButton>
          <div className="grid gap-6 md:grid-cols-[1fr,1.1fr]">
            {/* Left: Order summary */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Sale</h3>
              <div className="space-y-1 max-h-[50vh] overflow-y-auto pr-1">
                {orderItems.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    No items in the order.
                  </div>
                ) : (
                  orderItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between gap-3 py-2 border-b last:border-0"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-sm text-muted-foreground w-5 text-right shrink-0">
                          {item.quantity}
                        </span>
                        <div className="min-w-0">
                          <span className="text-sm font-medium truncate block">{item.name}</span>
                          {item.is_subsidy && (
                            <Badge variant="outline" className="h-4 text-[10px] mt-0.5">Subsidy</Badge>
                          )}
                        </div>
                      </div>
                      <span className={cn("text-sm font-medium shrink-0", item.is_subsidy && "text-green-600")}>
                        {formatCurrency(item.effective_price * item.quantity)}
                      </span>
                    </div>
                  ))
                )}
              </div>

              {/* Totals */}
              <div className="mt-3 pt-3 border-t space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatCurrency(cappedSubtotal)}</span>
                </div>
                {promotionDiscount > 0 && (
                  <div className="space-y-1">
                    {promotionResult.appliedPromotions.map(ap => (
                      <div key={ap.id} className="flex justify-between text-sm text-green-600">
                        <span className="flex items-center gap-1">
                          <Tag className="h-3 w-3" />
                          {ap.name}
                        </span>
                        <span>-{formatCurrency(ap.totalDiscount)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tax No Tax</span>
                  <span>{formatCurrency(tax)}</span>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t flex justify-between items-center">
                <span className="text-sm font-bold tracking-wide uppercase">
                  Sale Total <span className="text-muted-foreground font-normal normal-case ml-1">{orderItems.reduce((s, i) => s + i.quantity, 0)} items</span>
                </span>
                <span className={cn("text-lg font-bold", total === 0 && hasCappedSubsidies ? "text-green-600" : "")}>
                  {formatCurrency(total)}
                </span>
              </div>

              {hasCappedSubsidies && totalUnusedSubsidy > 0 && (
                <p className="text-xs text-amber-600 bg-amber-500/10 border border-amber-500/30 rounded-md p-2 mt-2">
                  Unused subsidy of {formatCurrency(totalUnusedSubsidy)} will be audited.
                </p>
              )}
            </div>

            {/* Right: Payment panel (Lightspeed-style) */}
            <div className="space-y-4">
              <DialogHeader className="space-y-3 p-0">
                <div className="flex items-center justify-between">
                  <DialogTitle className="text-2xl font-semibold">Pay</DialogTitle>
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={cashTendered}
                    onChange={(e) => setCashTendered(e.target.value)}
                    autoFocus
                    className="w-56 h-16 text-right text-4xl font-bold border-2 border-primary rounded-lg bg-background focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
                <DialogDescription className="text-xs text-muted-foreground text-right">
                  {checkoutPayments.length > 0
                    ? `${formatCurrencyCents(checkoutCollectedTotal)} collected. ${formatCurrencyCents(paymentBalanceDue)} remaining.`
                    : hasPartialCashTender
                      ? `Click Collect Cash to apply ${formatCurrencyCents(cashTenderedValue)} and keep the sale open for ${formatCurrencyCents(balanceAfterCashTender)}.`
                      : 'Edit to make a partial payment'}
                </DialogDescription>
              </DialogHeader>

              {checkoutPayments.length > 0 && (
                <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                  <div className="flex items-center justify-between text-sm font-medium">
                    <span>Collected</span>
                    <span>{formatCurrencyCents(checkoutCollectedTotal)}</span>
                  </div>
                  {checkoutPayments.map((payment) => (
                    <div key={payment.id} className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{payment.paymentTypeName}</span>
                      <span>{formatCurrencyCents(payment.amount)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between border-t pt-2 text-sm font-semibold">
                    <span>Balance due</span>
                    <span>{formatCurrencyCents(paymentBalanceDue)}</span>
                  </div>
                </div>
              )}

              {/* Payment method buttons */}
              <div className="grid grid-cols-3 gap-2">
                {(cashPaymentTypes.length === 0 || !hideDefaultPaymentTypes) && (
                  <Button
                    variant={selectedPaymentOption.isCash && !selectedPaymentOption.id ? "blaze" : "outline"}
                    className="h-12 text-base font-semibold gap-2"
                    onClick={() => {
                      setSelectedPaymentOption({ method: 'cash', isCash: true });
                    }}
                    disabled={isProcessingPayment || orderItems.length === 0}
                  >
                    Cash
                  </Button>
                )}

                {cashPaymentTypes.map((pt) => (
                  <Button
                    key={pt.id}
                    variant={
                      selectedPaymentOption.isCash && selectedPaymentOption.id === pt.id
                        ? "blaze"
                        : "outline"
                    }
                    className="h-12 text-base font-semibold gap-2"
                    onClick={() => {
                      setSelectedPaymentOption({
                        method: 'cash',
                        id: pt.id,
                        name: pt.name,
                        isCash: true,
                      });
                    }}
                    disabled={isProcessingPayment || orderItems.length === 0}
                  >
                    {pt.name}
                  </Button>
                ))}

                {(locationPaymentTypes.length === 0 || !hideDefaultPaymentTypes) && (
                  <Button
                    variant="outline"
                    className="h-12 text-base font-semibold gap-2"
                    onClick={() => handleNonCashPayment('card')}
                    disabled={isProcessingPayment || orderItems.length === 0}
                  >
                    {isProcessingPayment && !selectedPaymentOption.isCash ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                    EPAY
                  </Button>
                )}

                {nonCashPaymentTypes.map((pt) => (
                  <Button
                    key={pt.id}
                    variant="outline"
                    className="h-12 text-base font-semibold gap-2"
                    onClick={() => handleNonCashPayment('other', pt.id, pt.name)}
                    disabled={isProcessingPayment || orderItems.length === 0}
                  >
                    {isProcessingPayment ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CreditCard className="h-4 w-4" />
                    )}
                    {pt.name}
                  </Button>
                ))}
              </div>

              {/* Cash payment section */}
              {selectedPaymentOption.isCash && (
                <div className="space-y-3">
                  {cashTenderedValue > paymentBalanceDue && paymentBalanceDue > 0 && (
                    <div className="flex items-center justify-between rounded-lg border border-green-500/30 bg-green-500/10 p-3">
                      <span className="text-sm font-medium text-green-700 dark:text-green-400">Change due</span>
                      <span className="text-lg font-bold text-green-700 dark:text-green-400">
                        {formatCurrencyCents(cashChangeDue)}
                      </span>
                    </div>
                  )}

                  {hasPartialCashTender && paymentBalanceDue > 0 && (
                    <div className="flex items-center justify-between rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
                      <span className="text-sm font-medium text-amber-700 dark:text-amber-400">Amount short</span>
                      <span className="text-lg font-bold text-amber-700 dark:text-amber-400">
                        {formatCurrencyCents(balanceAfterCashTender)}
                      </span>
                    </div>
                  )}

                  <Button
                    variant="blaze"
                    className="w-full h-14 text-lg font-semibold gap-2"
                    onClick={() =>
                      handleCashPayment(selectedPaymentOption.id, selectedPaymentOption.name)
                    }
                    disabled={isProcessingPayment || orderItems.length === 0 || paymentBalanceDue <= PAYMENT_AMOUNT_TOLERANCE}
                  >
                    {isProcessingPayment ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <DollarSign className="h-5 w-5" />
                    )}
                    Collect {formatCurrencyCents(cashTenderedValue || paymentBalanceDue)} Cash
                  </Button>
                </div>
              )}

              {/* Secondary options */}
              <div className="border-t pt-3 space-y-2">
                <p className="text-xs text-muted-foreground">
                  Add a customer to pay with the following options:
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    className="h-10"
                    onClick={() => {
                      setIsCheckoutOpen(false);
                      setIsSplitPaymentOpen(true);
                    }}
                    disabled={isProcessingPayment || orderItems.length === 0 || checkoutPayments.length > 0}
                  >
                    Split Payment
                  </Button>
                  <Button
                    variant="outline"
                    className="h-10"
                    disabled
                  >
                    On Account
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Customer Modal */}
      <AddCustomerModal
        open={showCustomerModal}
        onOpenChange={setShowCustomerModal}
        onSelectCustomer={setSelectedCustomer}
        selectedCustomer={selectedCustomer}
        hideNewCustomerButton={isCashier}
      />

      {/* Modifier Selection Modal */}
      <ModifierSelectionModal
        open={modifierModalOpen}
        onOpenChange={setModifierModalOpen}
        product={selectedProductForModifiers}
        onConfirm={handleModifierConfirm}
      />

      {/* Split Payment Dialog */}
      <SplitPaymentDialog
        open={isSplitPaymentOpen}
        onOpenChange={setIsSplitPaymentOpen}
        orderTotal={total}
        availablePaymentTypes={[
          // Add default cash/card payment types (use _default_ prefix, converted to undefined in handler)
          ...(locationPaymentTypes.length === 0 || !hideDefaultPaymentTypes
            ? [
                { id: '_default_cash', name: 'Cash', method: 'cash' as const, is_cash_equivalent: true },
                { id: '_default_card', name: 'Card', method: 'card' as const, is_cash_equivalent: false },
              ]
            : []),
          // Add custom cash payment types
          ...cashPaymentTypes.map(pt => ({
            id: pt.id,
            name: pt.name,
            method: 'cash' as const,
            is_primary: pt.is_primary,
            is_cash_equivalent: true,
          })),
          // Add custom non-cash payment types
          ...nonCashPaymentTypes.map(pt => ({
            id: pt.id,
            name: pt.name,
            method: 'other' as const,
            is_primary: pt.is_primary,
            is_cash_equivalent: pt.is_cash_equivalent ?? false,
          })),
        ]}
        onProcessPayments={handleSplitPayment}
        isProcessing={isProcessingPayment}
      />

      {/* Stock Override PIN Dialog */}
      <ManagerPinDialog
        open={stockOverrideDialogOpen}
        onOpenChange={(open) => {
          setStockOverrideDialogOpen(open);
          if (!open) {
            setPendingStockOverride(null);
          }
        }}
        onVerified={handleStockOverrideApproved}
        title="Manager Approval Required"
        description={
          pendingStockOverride
            ? `Approve adding ${pendingStockOverride.quantity} ${pendingStockOverride.product.name}? This exceeds FOH stock (${pendingStockOverride.fohStock} available, ${pendingStockOverride.cartQuantity} in cart).`
            : 'Manager PIN required to override stock limit'
        }
      />

      {/* Remote Session Components */}
      {/* Banner when being remotely controlled */}
      <RemoteSessionBanner
        session={remoteSession}
        connectionState={remoteConnectionState}
        onEndSession={() => endRemoteSession.mutateAsync()}
        isEndingSession={endRemoteSession.isPending}
      />

      {/* Host widget (shows when remotely connected) */}
      <RemoteSessionHost
        session={remoteSession}
        connectionState={remoteConnectionState}
        localStream={remoteLocalStream}
        error={remoteError}
        onEndSession={() => endRemoteSession.mutateAsync()}
        isEndingSession={endRemoteSession.isPending}
      />

      {/* Incoming session request modal */}
      {remotePendingRequest && (
        <IncomingSessionRequest
          session={remotePendingRequest}
          onAccept={() => acceptSession.mutateAsync(remotePendingRequest.id)}
          onReject={() => rejectSession.mutateAsync(remotePendingRequest.id)}
          isAccepting={acceptSession.isPending}
          isRejecting={rejectSession.isPending}
          isScreenShareSupported={isScreenShareSupported}
        />
      )}
    </DashboardLayout>
  );
}
